import { Chord, Key } from 'chordsheetjs';
import type { ChartSection, ChartVariant, Progression } from '../chart/types.ts';

// Transpozycja, wg specyfikacji etapu 7
// (~/.claude/plans/tak-a-r-b-plan-humble-crane.md, transpozycja i kapodaster).
//
// ROZSTRZYGNIĘCIE Z NAGŁÓWKA SPECYFIKACJI, powtórzone tu, bo jest istotą tego
// modułu: transpozycja jest przesunięciem stosowanym PRZY RENDEROWANIU i
// NIGDY nie jest zapisywana do skompilowanego `Chart`. `signature` sekcji
// powstaje z nazw akordów (patrz `buildSignature` w `chart/progression.ts`),
// więc utrwalona transpozycja zmieniłaby sygnatury, przez to scalanie sekcji
// na poziomie degradacji 7, a przy okazji mapę układu. Miejsce w potoku jest
// więc jedno: `compile() -> compileVariants() -> transponujDoWyswietlenia() ->
// dopasuj()`, ZAWSZE na wariantach, PRZED każdym przebiegiem pomiarowym -
// nazwy akordów po transpozycji bywają dłuższe (`A` na `F#`, `E` na `Bb`),
// więc mierzony DOM musi zawierać dokładnie tę treść, którą zobaczy człowiek.

type Pisownia = '#' | 'b';

/** `nuta.length > 1` znaczy, że nazwa niesie znak chromatyczny (`C#`, `Db`) -
 * nuty naturalne (`C`, `D`...) mają zawsze jeden znak. Rozróżnienie jest tu
 * konieczne, bo `Key.useAccidental`/`Chord.useAccidental` WYMUSZA podaną
 * pisownię na KAŻDEJ nucie, także naturalnej: zmierzone na `Chord.parse('B')
 * .useAccidental('b').toString()`, które daje `Cb`, nie `B`. Wywołanie tego
 * bez warunku zamieniłoby naturalne akordy w utworze na przypadkowe
 * enharmoniczne potworki za każdym razem, gdy globalnie wybrana pisownia nie
 * zgadza się kierunkiem z akurat tą nutą. */
function potrzebujeWyboruPisowni(nuta: string): boolean {
  return znakZNazwyNuty(nuta) !== null;
}

/**
 * Nakłada `pisownia` WYŁĄCZNIE na tę część akordu (`root`, `bass`), która
 * naprawdę niesie znak chromatyczny, zostawiając drugą część (jeśli jest
 * naturalna) nietkniętą. `Chord.useAccidental` działa na całym akordzie
 * naraz, więc przy akordzie z basem, gdzie tylko jedna strona wymaga wyboru
 * (np. `Bm/A` transponowane na `C#m/B`: root wymaga wyboru, bas `B` jest
 * naturalny), naiwne wywołanie na całym akordzie wymusiłoby pisownię też na
 * naturalnym basie i zmieniłoby `B` w `Cb`. Stąd `Chord.set({ root, bass })`
 * zamiast `Chord.useAccidental()` wprost.
 */
function zastosujPisownie(akord: Chord, pisownia: Pisownia): Chord {
  const nowyRoot = akord.root !== null && potrzebujeWyboruPisowni(akord.root.note)
    ? akord.root.useAccidental(pisownia)
    : akord.root;
  const nowyBas = akord.bass !== null && potrzebujeWyboruPisowni(akord.bass.note)
    ? akord.bass.useAccidental(pisownia)
    : akord.bass;
  if (nowyRoot === akord.root && nowyBas === akord.bass) return akord;
  return akord.set({ root: nowyRoot, bass: nowyBas });
}

/**
 * Transponuje jeden token akordu o `delta` półtonów, przez `Chord` z
 * ChordSheetJS - nie własną arytmetyką. Nazwy akordów w tym korpusie mają
 * basy (`Bm/A`), przyrostki (`Asus2`, `Cmaj7`, `Em#5`) i obie pisownie znaków
 * chromatycznych, a własna implementacja pomyliłaby się na pierwszym z brzegu
 * przypadku. Gdy `Chord.parse` nie rozpozna tokenu (śmieć w źródle, "N.C."
 * albo literówka), token wraca BEZ ZMIAN - nigdy nie zgadujemy, a grający
 * zobaczy nietransponowany akord dokładnie tam, gdzie stał, co samo w sobie
 * jest informacją podniesioną do wywołującego (nie ma tu osobnego kanału
 * diagnostycznego, bo `transponujDoWyswietlenia` ma z założenia jeden wynik:
 * transponowany `ChartVariant`).
 */
function transponujTokenAkordu(token: string, delta: number, pisownia: Pisownia): string {
  const akord = Chord.parse(token.trim());
  if (akord === null) return token;
  return zastosujPisownie(akord.transpose(delta), pisownia).toString();
}

/**
 * Przeważająca pisownia znaków chromatycznych W ORYGINALNYCH akordach
 * utworu, licząc `root` i `bas` KAŻDEGO poprawnie sparsowanego akordu w
 * `progression.raw` wszystkich sekcji (nie dopasowaniem tekstowym na `#`/`b`
 * w stringu, bo litera „b" jako nazwa nuty B nie jest znakiem chromatycznym).
 * Używana, gdy tonacja utworu jest nieznana albo po transpozycji nie wymaga
 * żadnego znaku chromatycznego (np. z powrotem nuta naturalna) - wtedy fakt
 * "tonacja znana" niczego nie rozstrzyga o pisowni pozostałych akordów.
 * Remis rozstrzyga się na korzyść krzyżyków, zgodnie ze specyfikacją.
 */
function przewazajacaPisowniaWZrodle(sections: readonly ChartSection[]): Pisownia {
  let ostre = 0;
  let plaskie = 0;
  for (const sekcja of sections) {
    for (const token of sekcja.progression.raw) {
      const akord = Chord.parse(token.trim());
      if (akord === null) continue;
      const znakKorzenia = akord.root ? znakZNazwyNuty(akord.root.note) : null;
      if (znakKorzenia === '#') ostre += 1;
      else if (znakKorzenia === 'b') plaskie += 1;
      const znakBasu = akord.bass ? znakZNazwyNuty(akord.bass.note) : null;
      if (znakBasu === '#') ostre += 1;
      else if (znakBasu === 'b') plaskie += 1;
    }
  }
  return plaskie > ostre ? 'b' : '#';
}

/**
 * Pisownia zwyczajowa dla tonacji, po klasie wysokości dźwięku toniki i trybie.
 *
 * Tabela istnieje, bo biblioteka jej NIE MA i to jest pomiar, nie domysł:
 * `Key.parse('Cm').preferredAccidental` zwraca `null`, tak samo dla `C` i `Am`.
 * `preferredAccidental` mówi o znaku przy samej TONICE (`Eb` daje `b`, `F#m`
 * daje `#`), a nie o znakach PRZY TONACJI, a to drugie rozstrzyga o pisowni
 * pozostałych akordów utworu.
 *
 * Bez tej tabeli utwór w a-moll przeniesiony o trzy półtony wychodził jako
 * `Cm D# A# G#`, choć c-moll zapisuje się bemolami: `Cm Eb Bb Ab`. Zmierzone
 * na `bieszczadzkie-anioly` przy `+3`. Gitarzysta czytający `D#` zamiast `Eb`
 * dostaje nazwę, której w tej tonacji nikt nie używa.
 *
 * Wartości są zwyczajem notacyjnym z kwintowego koła: bemolowe tonacje durowe
 * to F, Bb, Eb, Ab, Db, Gb, a molowe ich względne, czyli Dm, Gm, Cm, Fm, Bbm,
 * Ebm. Tonacje bez znaków (C, Am) i te niejednoznaczne enharmonicznie
 * (Gb wobec F#) rozstrzygamy na krzyżyki, bo na gitarze częściej spotyka się
 * F# niż Gb.
 */
const PISOWNIA_TONACJI_DUR: readonly Pisownia[] =
  ['#', 'b', '#', 'b', '#', 'b', '#', '#', 'b', '#', 'b', '#'];
const PISOWNIA_TONACJI_MOLL: readonly Pisownia[] =
  ['b', '#', 'b', 'b', '#', 'b', '#', 'b', '#', '#', 'b', '#'];

const KLASA_NUTY: Readonly<Record<string, number>> =
  { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

/** Znak chromatyczny czytany Z SAMEJ NAZWY nuty, a nie z pola `accidental`.
 *
 * To jest pomiar, nie założenie: `Chord.parse('Bm/A').transpose(2)` daje
 * `root.note === 'C#'` przy `root.accidental === null`. Pole `accidental`
 * bywa więc puste także wtedy, gdy nuta znak NIESIE, a kod czytający tylko je
 * nie widzi ani jednego znaku w całym utworze. Wcześniejsza wersja liczenia
 * przewagi znaków w źródle miała dokładnie ten błąd: nie zliczała nigdy nic i
 * zawsze zwracała krzyżyki, czyli swoją wartość domyślną. */
function znakZNazwyNuty(nuta: string): '#' | 'b' | null {
  const ogon = nuta.slice(1);
  if (ogon.startsWith('#')) return '#';
  if (ogon.startsWith('b')) return 'b';
  return null;
}

/** Klasa wysokości dźwięku (0 = C) dla nazwy nuty w postaci, jaką daje
 * `Chord.root.note`, czyli razem ze znakiem chromatycznym. */
function klasaWysokosci(nuta: string): number | null {
  const podstawa = KLASA_NUTY[nuta.charAt(0).toUpperCase()];
  if (podstawa === undefined) return null;
  let przesuniecieZnaku = 0;
  for (const znak of nuta.slice(1)) {
    if (znak === '#') przesuniecieZnaku += 1;
    else if (znak === 'b') przesuniecieZnaku -= 1;
  }
  return (((podstawa + przesuniecieZnaku) % 12) + 12) % 12;
}

/** Czy akord jest molowy. `maj7` zaczyna się od tych samych liter co `m`, więc
 * samo sprawdzenie pierwszej litery przyrostka byłoby błędem. */
function jestMolowy(akord: Chord): boolean {
  const przyrostek = akord.suffix ?? '';
  return przyrostek.startsWith('m') && !przyrostek.startsWith('maj');
}

/** Pierwszy akord utworu, który da się sparsować. Używany jako przybliżenie
 * toniki, gdy utwór nie podaje tonacji wprost, a taka jest reguła w tym
 * korpusie: siedem z dziewięciu tabów nie ma ani jednej linii metadanych.
 * Przybliżenie jest zgrubne, ale dla pisowni wystarczające, bo chodzi o wybór
 * między dwoma zapisami tego samego dźwięku, a nie o analizę harmoniczną. */
function pierwszyAkord(sections: readonly ChartSection[]): Chord | null {
  for (const sekcja of sections) {
    for (const token of sekcja.progression.raw) {
      const akord = Chord.parse(token.trim());
      if (akord !== null) return akord;
    }
  }
  return null;
}

function pisowniaZToniki(akord: Chord, delta: number): Pisownia | null {
  const przeniesiony = akord.transpose(delta);
  const korzen = przeniesiony.root;
  if (!korzen) return null;
  const klasa = klasaWysokosci(korzen.note);
  if (klasa === null) return null;
  const tabela = jestMolowy(przeniesiony) ? PISOWNIA_TONACJI_MOLL : PISOWNIA_TONACJI_DUR;
  return tabela[klasa] ?? null;
}

/**
 * Ustala JEDNĄ pisownię chromatyczną dla całej transpozycji, PRZED
 * przeliczeniem jakiegokolwiek tokenu - to jest warunek determinizmu ze
 * specyfikacji: gdyby ta sama nazwa raz wychodziła jako `A#`, a raz jako `Bb`,
 * wynik pomiaru w `dopasuj()` zależałby od kolejności prób.
 *
 * Kolejność źródeł, od najpewniejszego: tonacja podana przez autora taba,
 * potem pierwszy akord utworu jako przybliżenie toniki, na końcu przewaga
 * znaków w oryginalnych akordach. Ostatnie źródło zostaje jako zabezpieczenie
 * dla utworów, w których nie da się sparsować ani tonacji, ani żadnego akordu.
 */
function wybierzPisownie(wariant: ChartVariant, delta: number): Pisownia {
  if (wariant.meta.key !== null) {
    const klucz = Chord.parse(wariant.meta.key);
    if (klucz !== null) {
      const zTonacji = pisowniaZToniki(klucz, delta);
      if (zTonacji !== null) return zTonacji;
    }
  }

  const pierwszy = pierwszyAkord(wariant.sections);
  if (pierwszy !== null) {
    const zAkordu = pisowniaZToniki(pierwszy, delta);
    if (zAkordu !== null) return zAkordu;
  }

  return przewazajacaPisowniaWZrodle(wariant.sections);
}

/** Transponuje tonację pokazywaną w `meta.key`. `null` zostaje `null`
 * (utwór bez podanej tonacji), a tonacja, której `Key.parse` nie rozpozna,
 * zostaje BEZ ZMIAN - z tego samego powodu co token akordu: nie zgadujemy. */
function transponujTonacje(key: string | null, delta: number, pisownia: Pisownia): string | null {
  if (key === null) return null;
  const klucz = Key.parse(key);
  if (klucz === null) return key;
  return klucz.transpose(delta).useAccidental(pisownia).toString();
}

function transponujProgresje(progresja: Progression, delta: number, pisownia: Pisownia): Progression {
  const przelicz = (token: string): string => transponujTokenAkordu(token, delta, pisownia);
  return {
    ...progresja,
    raw: progresja.raw.map(przelicz),
    base: progresja.base.map(przelicz),
    tail: progresja.tail.map(przelicz),
    phrases: progresja.phrases.map((fraza) => fraza.map(przelicz)),
  };
}

/** Transponuje jedną sekcję. Zmienia WYŁĄCZNIE `progression` - `id`,
 * `signature`, `labels`, `anchor`, `mergedCount`, `kind`, `isInstrumental`,
 * `tabOnly` i `description` zostają tymi samymi wartościami, bo powstały
 * PRZED transpozycją i decyzje kompilatora (scalanie, dziedziczenie) mają
 * zostać takie same niezależnie od tego, w jakiej wysokości się gra. */
function transponujSekcje(sekcja: ChartSection, delta: number, pisownia: Pisownia): ChartSection {
  return { ...sekcja, progression: transponujProgresje(sekcja.progression, delta, pisownia) };
}

/**
 * Transponuje `wariant` o `przesuniecie` półtonów, do pokazania grającemu.
 * `przesuniecie === 0` zwraca TEN SAM obiekt (tożsamość, nie kopia) - to jest
 * najczęstszy przypadek (utwór bez ustawionego stroju) i nie ma powodu, żeby
 * kosztował cokolwiek.
 *
 * Nie zmienia `id`, `signature`, `layoutMap`, kolejności sekcji, decyzji o
 * scaleniu ani `labels` - te powstały przed transpozycją i mają takie zostać.
 * `meta.capo` (kapodaster wpisany przez autora taba do źródła) też nie jest
 * tu ruszany: to jest ustawienie autora, transpozycja gracza jest osobnym
 * polem w `Utwor` (`store/db.ts`) i osobno pokazywana - zmieszanie tych dwóch
 * dałoby ciche nadpisanie tego, co napisał autor.
 */
export function transponujDoWyswietlenia(wariant: ChartVariant, przesuniecie: number): ChartVariant {
  if (przesuniecie === 0) return wariant;

  const pisownia = wybierzPisownie(wariant, przesuniecie);

  return {
    ...wariant,
    meta: { ...wariant.meta, key: transponujTonacje(wariant.meta.key, przesuniecie, pisownia) },
    sections: wariant.sections.map((sekcja) => transponujSekcje(sekcja, przesuniecie, pisownia)),
  };
}
